<script setup>
import { formApi } from '@/configs/forms-api';
import { ref, computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from '#app';
import { createApplicationSchema } from '@/configs/validation-schemas';
import { useGlobalStore } from '@/stores/global';
import { useThanksData } from '@/composables/useThanksData';
import { useRequest } from '@/composables/useRequest';
import { loadRecaptchaScript } from '@/composables/useRecaptcha';
const props = defineProps({
  type: { type: String, default: () => '' },
  services: { type: Array, default: () => [] },
});

const { t } = useI18n();
const thanksData = useThanksData();
const router = useRouter();
const route = useRoute();
const globalStore = useGlobalStore();
const applicationSchema = computed(() => createApplicationSchema(t));

//const emit = defineEmits(['success', 'fail']);

const loading = ref(false);

const captchaSiteKey = computed(() => globalStore.captchaSiteKey);

const { handleSubmit, resetForm, setErrors } = useForm({
  validationSchema: applicationSchema,
  initialValues: {
    name: '',
    phone: '',
    question: '',
    page: '',
  },
});
const name = useField('name');
const phone = useField('phone');

const phoneMaskRegExp = /^\+380 \d{2} \d{3} \d{4}$/;
const isRequiredFilled = computed(() => {
  if (!phoneMaskRegExp.test(phone.value.value || '')) return false;

  return applicationSchema.value.isValidSync({
    name: name.value.value,
    phone: phone.value.value,
  });
});

const getCaptchaToken = async () => {
  const loaded = await loadRecaptchaScript(captchaSiteKey.value);

  return new Promise((resolve) => {
    if (!loaded || !window.grecaptcha || !captchaSiteKey.value) {
      console.warn('ReCaptcha script not loaded or site key missing');
      resolve(null);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(captchaSiteKey.value, { action: 'sign_up_service' })
        .then((token) => resolve(token))
        .catch((err) => {
          console.error('ReCaptcha execution error:', err);
          resolve(null);
        });
    });
  });
};
const onSubmit = handleSubmit(async (values) => {
  loading.value = true;
  const currentFullUrl = process.client ? window.location.href : '';
  try {
    const token = await getCaptchaToken();
    const res = await useRequest(`/forms/${formApi['services_calculator']}`, {
      method: 'POST',
      body: {
        name: values.name,
        phone: values.phone,
        page: currentFullUrl,
        selected_services: props.services,
        token: token ? token : {},
      },
    });

    if (res && res.success !== false) {
      if (process.client) {
        window.dataLayer = window.dataLayer || [];
        const translatedFormName = t(`service_calculator.sign_up_service`);
        window.dataLayer.push({
          event: 'form_submit',
          form_name: translatedFormName,
          form_id: 'sign_up_service',
          form_location: currentFullUrl,
          form_status: 'success',
        });
      }

      resetForm();
      globalStore.closeApplicationPopup();
      thanksData.value = res;
      const langPrefix = route.params.lang ? `/${route.params.lang}` : '';
      router.push(`${langPrefix}/thanks`);
    } else if (res && res.errors) {
      setErrors(res.errors);
      console.warn(res.header || 'Validation Error');
    }
  } catch (e) {
    if (e.data && e.data.errors) {
      setErrors(e.data.errors);
    }
    console.error('Submission error:', e);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <form class="form-application" @submit.prevent="onSubmit">
    <div
      class="form-application__item form-application__item--name"
      :class="{ 'form-application__item--error': !!name.errorMessage.value }"
    >
      <FloatLabel variant="on" class="floatlabel-onwhite">
        <InputText
          id="applicationName"
          v-model="name.value.value"
          class="form-application__item-field"
          :invalid="!!name.errorMessage.value"
          type="text"
        />
        <label
          for="applicationName"
          class="form-application__item-label"
          :class="{ 'error-100': !!name.errorMessage.value }"
        >
          {{ $t('application_popup.first_name') }}*
        </label>
      </FloatLabel>
      <transition name="fade">
        <small
          v-if="name.errorMessage.value"
          class="form-application__error-text"
        >
          {{ name.errorMessage.value }}
        </small>
      </transition>
    </div>
    <div
      class="form-application__item form-application__item--phone"
      :class="{ 'form-application__item--error': !!phone.errorMessage.value }"
    >
      <FloatLabel variant="on" class="floatlabel-onwhite">
        <InputMask
          id="applicationPhone"
          v-model="phone.value.value"
          class="form-application__item-field"
          mask="+380 99 999 9999"
          placeholder="+380"
          fluid
          :invalid="!!phone.errorMessage.value"
        />
        <label
          for="applicationPhone"
          class="form-application__item-label"
          :class="{ 'error-100': !!phone.errorMessage.value }"
        >
          {{ $t('application_popup.phone') }}*
        </label>
      </FloatLabel>
      <transition name="fade">
        <small
          v-if="phone.errorMessage.value"
          class="form-application__error-text"
        >
          {{ phone.errorMessage.value }}
        </small>
      </transition>
    </div>
    <div class="form-application__bottom">
      <Button
        type="submit"
        class="form-application__button link-normal button-black js-button-services-calculator"
        :loading="loading"
        :disabled="!isRequiredFilled"
      >
        {{ $t('service_calculator.sign_up_service') }}
      </Button>
      <p
        v-if="globalStore.termsLink || globalStore.policyLink"
        class="mb-0 form-application__text label"
      >
        {{ $t('application_popup.agreement_start') }}
        {{ $t('application_popup.send_button')
        }}{{ $t('application.agreement_middle') }}
        <CTagDetect
          v-if="globalStore.termsLink"
          :to="globalStore.termsLink"
          class="text-decoration-underline"
        >
          {{ $t('application_popup.agreement_terms_of_use') }}
        </CTagDetect>
        <template v-else>
          {{ $t('application_popup.agreement_terms_of_use') }}
        </template>
        {{ $t('application_popup.agreement_between') }}
        <CTagDetect
          v-if="globalStore.policyLink"
          :to="globalStore.policyLink"
          class="text-decoration-underline"
        >
          {{ $t('application_popup.agreement_privacy_policy') }}
        </CTagDetect>
        <template v-else>
          {{ $t('application_popup.agreement_privacy_policy') }}
        </template>
      </p>
    </div>
  </form>
</template>

<style scoped lang="scss">
.form-application {
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;
  grid-template-areas: 'name' 'phone' 'bottom';
  @include media-breakpoint-up(sm) {
    gap: 16px;
    grid-template-columns: repeat(2, 6fr);
    grid-template-areas: 'name phone' 'bottom bottom';
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
  }
  &__error {
    &-text {
      @include label;
      color: $error;
    }
  }
  &__item {
    &--name {
      grid-area: name;
    }
    &--phone {
      grid-area: phone;
    }
    &-label {
      @include body-3-medium;
      color: $on-surface-variant-2 !important;
    }
    &-field {
      @include body-3-medium;
      color: $on-surface-variant-2 !important;
      padding: 13px 16px 13px 16px;
      width: 100%;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 13);
        @include responsive-spacing(padding-bottom, 13);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
      }
    }
    &--error {
      .form-application__item-label {
        color: $error !important;
      }
    }
  }
  &__bottom {
    align-items: start;
    display: grid;
    gap: 16px;
    grid-area: bottom;
    padding-top: 8px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 24px;
      padding-top: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
      @include responsive-spacing(padding-top, 24);
    }
  }
  &__button {
    margin: 0 auto;
    width: fit-content;
  }
}
</style>
